import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatTL } from "@/lib/api";
import { Building2, Wallet, ShoppingBag, Percent, Eye, Tag } from "lucide-react";
import OrderDetailModal from "@/components/OrderDetailModal";

export default function B2BDashboard() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [special, setSpecial] = useState([]);

  useEffect(() => {
    if (user?.role !== "b2b_dealer") return;
    api.get("/orders/my").then(r => setOrders(r.data));
    api.get("/dealer/special-prices").then(r => setSpecial(r.data));
  }, [user]);

  if (loading) return <div className="kz-container py-20 text-center">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace/>;
  if (user.role !== "b2b_dealer" || user.b2b_status !== "approved") {
    return (
      <div className="kz-container py-16 text-center">
        <h1 className="font-display text-2xl font-black mb-2">Bayi Paneli</h1>
        <p className="text-ink-500 mb-4">Bayi paneline erişim için onaylı bayi olmanız gerekiyor.</p>
        <Link to="/bayi-basvuru" className="kz-btn-primary">Bayi Başvurusu</Link>
      </div>
    );
  }

  return (
    <div className="kz-container py-10">
      <div className="flex items-center gap-3 mb-2">
        <Building2 className="text-b2b" size={28}/>
        <h1 className="font-display text-3xl md:text-4xl font-black">Bayi Paneli</h1>
      </div>
      <p className="text-ink-500 mb-8">{user.company_name}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="kz-card p-5 border-l-4 border-b2b">
          <Wallet className="text-b2b mb-2"/>
          <div className="text-xs uppercase text-ink-500">Cari Bakiye</div>
          <div className={`font-display font-black text-2xl ${user.credit_balance < 0 ? "text-red-600" : ""}`}>{formatTL(user.credit_balance)}</div>
          <div data-testid="b2b-available-limit" className="text-[11px] text-ink-500">Kullanılabilir limit: <b className="text-ink-900">{formatTL((user.credit_balance || 0) + (user.credit_limit || 0))}</b></div>
          <Link to="/hesabim" className="text-xs text-b2b hover:underline">Ekstre gör →</Link>
        </div>
        <div className="kz-card p-5">
          <Percent className="text-brand mb-2"/>
          <div className="text-xs uppercase text-ink-500">İndirim Oranı</div>
          <div className="font-display font-black text-2xl">%{user.b2b_discount}</div>
        </div>
        <div className="kz-card p-5">
          <ShoppingBag className="text-ink-700 mb-2"/>
          <div className="text-xs uppercase text-ink-500">Toplam Sipariş</div>
          <div className="font-display font-black text-2xl">{orders.length}</div>
        </div>
        <div className="kz-card p-5">
          <div className="text-xs uppercase text-ink-500 mb-2">Toplam Harcama</div>
          <div className="font-display font-black text-2xl">{formatTL(orders.reduce((s,o)=>s+(o.payment_status==="paid"?o.total:0),0))}</div>
        </div>
      </div>

      <h2 className="font-display text-2xl font-black mb-4">Sipariş Geçmişi & Takip</h2>
      <div className="kz-card overflow-x-auto">
        <table className="w-full">
          <thead className="bg-ink-100">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="py-3 px-4">Sipariş No</th>
              <th className="py-3 px-4">Tarih</th>
              <th className="py-3 px-4">Adet</th>
              <th className="py-3 px-4">Ödeme</th>
              <th className="py-3 px-4">Durum</th>
              <th className="py-3 px-4 text-right">Tutar</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan="7" className="py-10 text-center text-ink-500">Henüz sipariş yok</td></tr>}
            {orders.map((o, i) => (
              <tr key={o.id} className={i % 2 ? "bg-ink-100/50" : ""}>
                <td className="py-3 px-4">
                  <button data-testid={`b2b-order-${o.order_number}`} onClick={()=>setSelectedOrder(o)} className="font-mono text-xs text-b2b hover:underline">{o.order_number}</button>
                </td>
                <td className="py-3 px-4 text-sm">{new Date(o.created_at).toLocaleDateString("tr-TR")}</td>
                <td className="py-3 px-4 text-sm">{o.items.length}</td>
                <td className="py-3 px-4 text-xs uppercase">{o.payment_method === "cari" ? "Cari" : o.payment_method === "havale" ? "Havale" : "Kart"}</td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] px-2 py-0.5 font-bold ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                    {o.payment_status.toUpperCase()}
                  </span>
                  <span className="ml-1 text-[10px] px-2 py-0.5 font-bold bg-ink-100">{o.order_status.toUpperCase()}</span>
                </td>
                <td className="py-3 px-4 text-right font-bold">{formatTL(o.total)}</td>
                <td className="py-3 px-4">
                  <button data-testid={`b2b-view-${o.order_number}`} onClick={()=>setSelectedOrder(o)} className="kz-btn-outline text-xs py-1.5 px-3"><Eye size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {special.length > 0 && (
        <div data-testid="b2b-special-prices" className="mt-8">
          <h2 className="font-display text-2xl font-black mb-4 flex items-center gap-2"><Tag className="text-b2b"/> Size Özel Fiyatlar</h2>
          <div className="kz-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-100"><tr className="text-left text-xs uppercase tracking-wider"><th className="py-3 px-4">SKU</th><th className="py-3 px-4">Ürün</th><th className="py-3 px-4 text-right">Liste (KDV hariç)</th><th className="py-3 px-4 text-right">Size Özel Net</th></tr></thead>
              <tbody>
                {special.map((x, i) => (
                  <tr key={x.id} className={i % 2 ? "bg-ink-100/50" : ""}>
                    <td className="py-2 px-4 font-mono text-xs">{x.sku}</td>
                    <td className="py-2 px-4">{x.name}</td>
                    <td className="py-2 px-4 text-right text-ink-500 line-through">{formatTL(x.list_price_try)}</td>
                    <td className="py-2 px-4 text-right font-bold text-b2b">{formatTL(x.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 p-6 bg-b2b/10 border border-b2b/30">
        <h3 className="font-display font-bold mb-2">Hızlı Sipariş İçin</h3>
        <p className="text-sm text-ink-700 mb-4">Kataloğumuzdan SKU ile arama yaparak hızlıca sepete ekleyebilirsiniz. Tüm fiyatlar bayi indiriminiz ile gösterilir.</p>
        <Link to="/katalog" className="kz-btn-dark inline-flex">Kataloğa Git</Link>
      </div>

      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={()=>setSelectedOrder(null)}/>}
    </div>
  );
}
